import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { LogRecord, LogContext, LoggingMetrics, AuthFailureQueue } from '../logging/models';
import { formatLogRecord } from '../logging/utilities/format-log-record.util';
import { redactAuthenticationData } from '../logging/utilities/redact-sensitive-data.util';
import { validateLoggingConfig, getEffectiveConfig } from '../logging/utilities/config-validation.util';
import { environment } from 'src/environments/environment';
import { 
  PRODUCTION_LOGGING_CONFIG, 
  DEVELOPMENT_LOGGING_CONFIG, 
  STAGING_LOGGING_CONFIG,
  LoggingConfig 
} from '../logging/models/logging-config.interface';
import { CLOUDWATCH_ENDPOINTS, LOG_GROUP_NAMES } from '../logging/constants';
import { AuthFailureQueueService } from '../logging/services/auth-failure-queue.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/**
 * LoggingService orchestrates the application's logging system.
 * It intercepts console methods, manages log processing, and coordinates
 * with other logging services.
 * 
 * Designed for reliability: many operations fail silently to avoid blocking
 * application execution.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggingService implements OnDestroy {
  // Original console method references for fallback
  private originalConsole: {
    log: (...args: any[]) => void;
    info: (...args: any[]) => void;
    warn: (...args: any[]) => void;
    error: (...args: any[]) => void;
    debug: (...args: any[]) => void;
    table?: (data: any) => void;
  };

  // Re-entrancy protection
  private isProcessingLog = false;
  private reentrancyTimeout: any;
  private readonly REENTRANCY_TIMEOUT = 5000; // 5 seconds
  
  // CloudWatch processing protection (separate from console re-entrancy)
  private processingCloudWatchLogs = new Set<string>();

  // CloudWatch transmission state
  private cloudWatchEnabled = false;
  
  // Flag to identify logging system internal logs
  private readonly LOGGING_SYSTEM_TAG = '[LOGGING_SYSTEM]';
  
  // Prevent multiple console bridging
  private static consoleBridged = false;
  
  // Emergency circuit breaker for infinite loops
  private recentLogMessages = new Set<string>();
  private readonly MAX_DUPLICATE_LOGS = 3;
  private duplicateDetectionEnabled = true;

  // Environment detection and configuration
  private environment: string;
  private enableConsoleOutput: boolean;
  private loggingConfig: LoggingConfig;

  // Authentication cache to prevent repeated checks
  private authCheckCache: { result: boolean; timestamp: number } | null = null;
  private readonly AUTH_CACHE_DURATION = 30000; // 30 seconds

  // Periodic authentication retry mechanism
  private authRetryInterval: any = null;
  private readonly AUTH_RETRY_INTERVAL_MS = 30000; // 30 seconds

  // Lazy injection to avoid circular dependencies
  private injector: Injector;
  private contextService: any = null;
  private logQueueManager: any = null;
  private amplifyService: any = null;
  private router: Router | null = null;

  // Router navigation handling
  private destroy$ = new Subject<void>();
  private routeFlushEnabled = true;

  // AuthFailureQueue for handling logs during authentication failures
  private authFailureQueue: AuthFailureQueue;

  // Logout state management
  private isLogoutInProgress = false;
  private suppressAuthErrors = false;

  // Cached module references to avoid repeated dynamic imports
  private writeLoggingServiceModule: any = null;
  private writeLoggingServiceChecked = false;

  // Comprehensive error handling and metrics tracking
  private errorMetrics = {
    queueOperationErrors: 0,
    authenticationStateErrors: 0,
    queueCorruptionRecoveries: 0,
    memoryIssueRecoveries: 0,
    totalAuthFailures: 0,
    totalQueuedLogs: 0,
    totalFlushedLogs: 0,
    lastErrorTimestamp: null as string | null,
    lastRecoveryTimestamp: null as string | null
  };

  // Error recovery state
  private isRecoveringFromError = false;
  private errorRecoveryAttempts = 0;
  private readonly MAX_RECOVERY_ATTEMPTS = 3;

  constructor(injector: Injector) {
    this.injector = injector;
    
    // Initialize AuthFailureQueue with error handling
    try {
      this.authFailureQueue = new AuthFailureQueueService();
      this.logInternalOperation('AuthFailureQueue initialized successfully');
    } catch (error) {
      this.handleQueueInitializationError(error);
    }
    
    // Store original console methods before bridging
    this.originalConsole = {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: console.debug.bind(console),
      table: console.table ? console.table.bind(console) : undefined
    };

    // Detect environment and load appropriate configuration
    this.environment = this.detectEnvironment();
    this.loggingConfig = this.getEnvironmentLoggingConfig();
    this.enableConsoleOutput = this.loggingConfig.enableConsoleOutput ?? this.shouldEnableConsoleOutput();

    // Validate configuration matches environment expectations
    this.validateConsoleConfiguration();

    // Initialize services with lazy loading
    this.initializeServices();
    
    // Initialize router navigation handling
    this.initializeRouterHandling();
    
    // Start periodic authentication retry mechanism
    this.startAuthenticationRetryMechanism();
    
    // Expose test method globally for debugging (dev only)
    if (this.environment === 'dev') {
      (window as any).testCloudWatchLogging = () => this.testCloudWatchLogging();
      (window as any).loggingService = this;
      (window as any).enableCloudWatchLogging = () => this.enableCloudWatchLogging();
      (window as any).getLoggingStatus = () => this.getLoggingStatus();
      (window as any).forceEnableCloudWatch = () => this.forceEnableCloudWatch();
      (window as any).enableCloudWatchTesting = () => this.enableCloudWatchLoggingAsync();
      (window as any).testLogFlow = () => this.testLogFlow();
      (window as any).emergencyStopLogging = () => this.emergencyDisableLogging();
      (window as any).getQueueStatus = () => this.getQueueStatus();
      (window as any).forceFlushQueue = () => this.forceFlushQueue();
      (window as any).disableDuplicateDetection = () => this.disableDuplicateDetection();
      (window as any).enableDuplicateDetection = () => this.enableDuplicateDetection();
      (window as any).clearAuthCache = () => this.clearAuthCache();
      (window as any).clearProcessingSet = () => this.clearProcessingSet();
      (window as any).disableAllLogging = () => this.disableAllLogging();
      (window as any).enableAllLogging = () => this.enableAllLogging();
      (window as any).setLoginInProgress = (inProgress: boolean) => this.setLoginInProgress(inProgress);
      (window as any).setLogoutInProgress = (inProgress: boolean) => this.setLogoutInProgress(inProgress);
      (window as any).shouldSuppressAuthErrors = () => this.shouldSuppressAuthErrors();
      (window as any).retryServiceInitialization = () => this.retryServiceInitialization();
      
      // Development debugging commands
      (window as any).checkLoggingStatus = () => this.checkLoggingStatus();
      (window as any).debugLogTransmission = () => this.debugLogTransmission();
      (window as any).testLogging = () => this.testLogging();
      
      (window as any).getFailedLogsStatus = () => {
        if (this.logQueueManager && typeof this.logQueueManager.getFailedLogsStatus === 'function') {
          return this.logQueueManager.getFailedLogsStatus();
        } else {
          return 'LogQueueManager not available';
        }
      };
      
      (window as any).retryFailedLogs = () => {
        if (this.logQueueManager && typeof this.logQueueManager.manualRetryFailedLogs === 'function') {
          return this.logQueueManager.manualRetryFailedLogs();
        } else {
          this.internalLog('warn', 'LogQueueManager not available for manual retry');
        }
      };
      
      (window as any).clearFailedLogs = () => {
        if (this.logQueueManager && typeof this.logQueueManager.clearStoredFailedLogs === 'function') {
          this.logQueueManager.clearStoredFailedLogs();
          this.internalLog('log', 'Failed logs cleared from localStorage');
        } else {
          this.internalLog('warn', 'LogQueueManager not available');
        }
      };
      
      // Interceptor debugging
      (window as any).testKinectiveHeaders = () => {
        const context = this.getContext();
        this.internalLog('log', 'Current context for headers:', context);
        return context;
      };
      (window as any).testWriteLoggingService = () => {
        if (this.logQueueManager && typeof this.logQueueManager.testWriteLoggingService === 'function') {
          return this.logQueueManager.testWriteLoggingService();
        } else {
          this.internalLog('warn', 'LogQueueManager or testWriteLoggingService not available');
        }
      };
      (window as any).getCloudWatchStatus = () => {
        const status = this.getLoggingStatus();
        this.internalLog('log', 'CloudWatch Status:', {
          enabled: status.cloudWatchEnabled,
          logQueueManager: status.logQueueManagerAvailable,
          servicesInitialized: status.servicesInitialized
        });
      };
      (window as any).forceStopCloudWatch = () => this.forceStopCloudWatch();
      
      // Emergency shutdown options
      (window as any).emergencyShutdown = () => {
        // Import WriteLoggingService dynamically to call static method
        import('../logging/services/write-logging.service').then(module => {
          module.WriteLoggingService.nuclearShutdownActivate();
          this.internalLog('error', 'Emergency shutdown activated - WriteLoggingService disabled');
        }).catch(() => {
          this.internalLog('error', 'Failed to activate emergency shutdown');
        });
      };
      
      (window as any).emergencyReset = () => {
        // Import WriteLoggingService dynamically to call static method
        import('../logging/services/write-logging.service').then(module => {
          module.WriteLoggingService.nuclearShutdownReset();
          this.internalLog('log', 'Emergency shutdown reset - WriteLoggingService re-enabled');
        }).catch(() => {
          this.internalLog('error', 'Failed to reset emergency shutdown');
        });
      };
      (window as any).getCircuitBreakerStatus = () => this.getCircuitBreakerStatus();
      
      // Console fallback controls
      (window as any).enableConsoleFallback = () => this.enableConsoleFallback();
      (window as any).disableConsoleFallback = () => this.disableConsoleFallback();
      (window as any).getConsoleStatus = () => this.getConsoleStatus();
      (window as any).testCloudWatchVsConsole = () => this.testCloudWatchVsConsole();
      
      // EMERGENCY: Restore original console methods
      (window as any).restoreOriginalConsole = () => this.restoreOriginalConsole();
      (window as any).emergencyDisableLogging = () => this.emergencyDisableLogging();
      
      // Circuit breaker controls
      (window as any).resetCircuitBreaker = () => {
        try {
          if (this.logQueueManager && this.logQueueManager.writeLoggingService && 
              typeof this.logQueueManager.writeLoggingService.resetCircuitBreaker === 'function') {
            this.logQueueManager.writeLoggingService.resetCircuitBreaker();
            this.internalLog('log', 'Circuit breaker reset to CLOSED state');
          } else {
            this.internalLog('warn', 'WriteLoggingService circuit breaker reset not available');
          }
        } catch (error) {
          this.internalLog('error', 'Error resetting circuit breaker: ' + error);
        }
      };
      
      // CORS debugging commands
      (window as any).testCorsRequest = () => {
        this.internalLog('log', 'Testing CORS request to CloudWatch API...');
        if (this.logQueueManager && this.logQueueManager.writeLoggingService) {
          // Force a test request to see CORS behavior
          this.info('CORS test log', { test: true, timestamp: new Date().toISOString() });
        }
      };
      
      (window as any).disableCloudWatchTemporarily = () => {
        this.cloudWatchEnabled = false;
        this.enableConsoleOutput = true;
        this.internalLog('log', 'CloudWatch temporarily disabled - using console only');
      };
      
      (window as any).clearPendingLogs = () => {
        try {
          if (this.logQueueManager && this.logQueueManager.writeLoggingService && 
              typeof this.logQueueManager.writeLoggingService.clearPendingLogs === 'function') {
            this.logQueueManager.writeLoggingService.clearPendingLogs();
            this.internalLog('log', 'Pending logs cleared');
          } else {
            this.internalLog('warn', 'WriteLoggingService clearPendingLogs not available');
          }
        } catch (error) {
          this.internalLog('error', 'Error clearing pending logs: ' + error);
        }
      };
      
      // AuthFailureQueue debugging commands
      (window as any).getAuthFailureQueueStats = () => this.getAuthFailureQueueStats();
      (window as any).clearAuthFailureQueue = () => this.clearAuthFailureQueue();
      (window as any).flushAuthFailureQueue = () => this.flushAuthFailureQueue();
      (window as any).stopAuthRetry = () => this.stopAuthenticationRetryMechanism();
      (window as any).startAuthRetry = () => this.startAuthenticationRetryMechanism();
      (window as any).notifyAuthRestored = () => this.notifyAuthenticationRestored();
      
      // Comprehensive error handling and metrics debugging commands
      (window as any).getErrorMetricsReport = () => this.getErrorMetricsReport();
      (window as any).resetErrorMetrics = () => this.resetErrorMetrics();
      (window as any).performHealthCheck = () => this.performHealthCheck();
      (window as any).testQueueRecovery = () => {
        // Simulate queue error for testing recovery
        this.handleQueueInitializationError(new Error('Test queue error'));
        return this.getErrorMetricsReport();
      };
      (window as any).testMemoryIssues = () => {
        // Simulate memory issues for testing recovery
        this.handleMemoryIssues();
        return this.getErrorMetricsReport();
      };
      (window as any).simulateAuthFailure = () => {
        // Simulate authentication failure for testing
        const testLog: LogRecord = {
          timestamp: new Date().toISOString(),
          level: 'INFO' as const,
          message: 'Test log for auth failure simulation',
          thread: 'main',
          kinective: {
            sessionId: 'test-session',
            product: 'Ground Control',
            environment: this.environment,
            service: 'Ground Control',
            correlationId: 'test-correlation-' + Date.now()
          }
        };
        this.handleAuthenticationFailureWithLogging(testLog);
        return this.getAuthFailureQueueStats();
      };
      
      // Route-based log flushing controls
      (window as any).enableRouteFlush = () => this.setRouteFlushEnabled(true);
      (window as any).disableRouteFlush = () => this.setRouteFlushEnabled(false);
      (window as any).isRouteFlushEnabled = () => this.isRouteFlushEnabled();
      (window as any).testRouteFlush = () => this.flushOnRouteChange();
      (window as any).testAuthFailureQueue = () => {
        // Temporarily disable CloudWatch to test queue
        const originalCloudWatchEnabled = this.cloudWatchEnabled;
        this.cloudWatchEnabled = false;
        
        this.info('Test log 1 - should be queued', { test: true });
        this.warn('Test log 2 - should be queued', { test: true });
        this.error('Test log 3 - should be queued', { test: true });
        
        const stats = this.getAuthFailureQueueStats();
        this.internalLog('log', 'Queue stats after test logs:', stats);
        
        // Restore CloudWatch state
        this.cloudWatchEnabled = originalCloudWatchEnabled;
        
        return stats;
      };
      
      (window as any).getLoggingStatus = () => {
        const circuitStatus = this.getCircuitBreakerStatus();
        const consoleStatus = this.getConsoleStatus();
        const loggingStatus = this.getLoggingStatus();
        
        return {
          circuit: circuitStatus,
          console: consoleStatus,
          logging: loggingStatus,
          timestamp: new Date().toISOString()
        };
      };
    }
  }

  /**
   * Bridge console methods to intercept all console output
   */
  bridgeConsole(): void {
    // Prevent multiple bridging which causes infinite loops
    if (LoggingService.consoleBridged) {
      this.internalLog('warn', 'Console already bridged, skipping to prevent infinite loops');
      return;
    }

    // Ensure we have original methods before bridging
    this.captureOriginalConsoleMethods();

    // Mark console as bridged BEFORE overriding to prevent re-entrancy
    LoggingService.consoleBridged = true;

    // Override console methods with lightweight wrappers that buffer calls
    console.log = (...args: any[]) => this.bufferConsoleCall('INFO', args);
    console.info = (...args: any[]) => this.bufferConsoleCall('INFO', args);
    console.warn = (...args: any[]) => this.bufferConsoleCall('WARN', args);
    console.error = (...args: any[]) => this.bufferConsoleCall('ERROR', args);
    console.debug = (...args: any[]) => this.bufferConsoleCall('DEBUG', args);
    
    this.internalLog('log', 'Console bridging completed with auto-suppression');
  }

  // Auto-suppression state
  private logVolumeCount = 0;
  private logVolumeResetTime = 0;
  private autoSuppressed = false;
  private autoSuppressTimeout: any = null;
  private readonly VOLUME_WINDOW_MS = 300;
  private readonly VOLUME_THRESHOLD = 10;
  private readonly SUPPRESS_DURATION_MS = 2000;

  /**
   * Auto-suppress console output when log volume spikes.
   */
  private shouldAutoSuppress(): boolean {
    const now = Date.now();

    if (now - this.logVolumeResetTime > this.VOLUME_WINDOW_MS) {
      this.logVolumeCount = 0;
      this.logVolumeResetTime = now;
    }

    this.logVolumeCount++;

    if (this.logVolumeCount >= this.VOLUME_THRESHOLD) {
      if (!this.autoSuppressed) {
        this.autoSuppressed = true;
        this.originalConsole.warn('[LOGGING] Auto-suppressing console output for ' + (this.SUPPRESS_DURATION_MS / 1000) + 's');
      }
      clearTimeout(this.autoSuppressTimeout);
      this.autoSuppressTimeout = setTimeout(() => {
        this.autoSuppressed = false;
        this.originalConsole.log('[LOGGING] Console output resumed');
      }, this.SUPPRESS_DURATION_MS);
    }

    return this.autoSuppressed;
  }

  // Buffered console drain
  private logBuffer: Array<{ level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG'; args: any[] }> = [];
  private drainScheduled = false;
  private readonly DRAIN_BATCH_SIZE = 3;
  private readonly DRAIN_INTERVAL_MS = 100;
  private readonly MAX_BUFFER_SIZE = 30;

  /**
   * Buffer a console call. No-op when auto-suppressed.
   */
  private bufferConsoleCall(level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG', args: any[]): void {
    // Skip entirely when suppressed (errors always pass through)
    if (this.autoSuppressed && level !== 'ERROR') {
      this.logVolumeCount++;
      return;
    }

    if (this.shouldAutoSuppress() && level !== 'ERROR') {
      return;
    }

    // Errors bypass the buffer
    if (level === 'ERROR') {
      try {
        this.getOriginalConsoleMethod(level)(...this.safeArgs(args));
      } catch (_) { /* never block */ }
      this.scheduleCloudWatch(level, args);
      return;
    }

    if (this.logBuffer.length >= this.MAX_BUFFER_SIZE) {
      return;
    }

    this.logBuffer.push({ level, args });

    if (!this.drainScheduled) {
      this.drainScheduled = true;
      setTimeout(() => this.drainLogBuffer(), this.DRAIN_INTERVAL_MS);
    }
  }

  /**
   * Drain buffered logs in small batches, yielding between each.
   */
  private drainLogBuffer(): void {
    this.drainScheduled = false;

    const batch = this.logBuffer.splice(0, this.DRAIN_BATCH_SIZE);
    for (const entry of batch) {
      this.handleConsoleCall(entry.level, entry.args, this.getOriginalConsoleMethod(entry.level));
    }

    if (this.logBuffer.length > 0) {
      this.drainScheduled = true;
      setTimeout(() => this.drainLogBuffer(), this.DRAIN_INTERVAL_MS);
    }
  }

  /**
   * Sanitize args for browser console. Avoids rendering large objects
   * which is the primary cause of UI freezes.
   */
  private safeArgs(args: any[]): any[] {
    return args.map(arg => {
      if (arg === null || arg === undefined) return arg;
      if (typeof arg === 'string') {
        return arg.length > 500 ? arg.substring(0, 500) + '…' : arg;
      }
      if (typeof arg !== 'object') return arg;

      if (Array.isArray(arg)) {
        if (arg.length > 5) return `[Array(${arg.length})]`;
        return arg.map(item => typeof item === 'object' && item !== null
          ? `{${Object.keys(item).slice(0, 3).join(',')}…}`
          : item
        );
      }

      const keys = Object.keys(arg);
      if (keys.length > 10) {
        return `{Object(${keys.length} keys: ${keys.slice(0, 4).join(', ')}…)}`;
      }
      return arg;
    });
  }

  /**
   * Capture truly original console methods
   */
  private captureOriginalConsoleMethods(): void {
    // Try to get the native console methods from the global object
    try {
      const nativeConsole = (window as any).console || console;
      
      // Store the current methods as original (they should be the real ones)
      this.originalConsole = {
        log: nativeConsole.log.bind(nativeConsole),
        info: nativeConsole.info.bind(nativeConsole),
        warn: nativeConsole.warn.bind(nativeConsole),
        error: nativeConsole.error.bind(nativeConsole),
        debug: nativeConsole.debug.bind(nativeConsole),
        table: nativeConsole.table ? nativeConsole.table.bind(nativeConsole) : undefined
      };
    } catch (error) {
      // Silent Failure
    }
  }

  // Rate limiting for CloudWatch logging
  private cloudWatchLogCount = 0;
  private cloudWatchLogResetTime = 0;
  private readonly MAX_CLOUDWATCH_LOGS_PER_SECOND = 10;

  /**
   * Process a buffered console call with auto-suppression and safe rendering.
   */
  private handleConsoleCall(
    level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG',
    args: any[],
    originalMethod: (...args: any[]) => void
  ): void {
    const safeConsoleMethod = this.getOriginalConsoleMethod(level);
    
    const shouldOutputToConsole = this.enableConsoleOutput && 
      (this.environment === 'dev' || this.environment === 'unknown');
    
    if ((window as any).loggingDisabled) {
      if (shouldOutputToConsole) {
        safeConsoleMethod(...this.safeArgs(args));
      }
      return;
    }

    if (this.isLoginInProgress()) {
      if (shouldOutputToConsole && !this.shouldAutoSuppress()) {
        safeConsoleMethod(...this.safeArgs(args));
      }
      return;
    }

    if (shouldOutputToConsole && !this.shouldAutoSuppress()) {
      try {
        safeConsoleMethod(...this.safeArgs(args));
      } catch (_) { /* never block */ }
    }
    
    this.scheduleCloudWatch(level, args);
  }

  /**
   * Enqueue a log for CloudWatch. Summarizes large payloads upfront
   * so formatLogRecord + redaction never process heavy objects.
   */
  private scheduleCloudWatch(level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG', args: any[]): void {
    if (!this.cloudWatchEnabled) return;

    if (level !== 'ERROR') {
      const now = Date.now();
      if (now - this.cloudWatchLogResetTime > 1000) {
        this.cloudWatchLogCount = 0;
        this.cloudWatchLogResetTime = now;
      }
      if (this.cloudWatchLogCount >= this.MAX_CLOUDWATCH_LOGS_PER_SECOND) {
        return;
      }
      this.cloudWatchLogCount++;
    }

    const lightArgs = this.summarizeForCloudWatch(args);

    setTimeout(() => {
      if (!this.isProcessingLog) {
        this.processLogForTransmission(level, lightArgs).catch(() => {});
      }
    }, 50);
  }

  // Max number of keys/items to fully serialize before summarizing the rest
  private readonly CW_MAX_KEYS = 10;
  private readonly CW_MAX_ARRAY_ITEMS = 3;
  private readonly CW_MAX_STRING_LENGTH = 2000;
  private readonly CW_MAX_VALUE_LENGTH = 200;

  /**
   * Prepare args for CloudWatch. Keeps real data up to a safe size,
   * then summarizes the rest so redaction never processes large payloads.
   */
  private summarizeForCloudWatch(args: any[]): any[] {
    return args.map(arg => this.trimForCloudWatch(arg));
  }

  /**
   * Recursively trim a value to a safe size for CloudWatch processing.
   * Real data is preserved up to the configured limits.
   */
  private trimForCloudWatch(val: any, depth = 0): any {
    if (val === null || val === undefined) return val;
    if (depth > 3) return typeof val === 'object' ? this.shallowSummary(val) : val;

    if (typeof val === 'string') {
      return val.length > this.CW_MAX_STRING_LENGTH
        ? val.substring(0, this.CW_MAX_STRING_LENGTH) + `… [${val.length} chars total]`
        : val;
    }

    if (typeof val !== 'object') return val;

    if (Array.isArray(val)) {
      if (val.length <= this.CW_MAX_ARRAY_ITEMS) {
        return val.map(item => this.trimForCloudWatch(item, depth + 1));
      }
      const kept = val.slice(0, this.CW_MAX_ARRAY_ITEMS).map(item => this.trimForCloudWatch(item, depth + 1));
      kept.push(`… and ${val.length - this.CW_MAX_ARRAY_ITEMS} more items`);
      return kept;
    }

    // Object
    const keys = Object.keys(val);
    if (keys.length <= this.CW_MAX_KEYS) {
      const result: any = {};
      for (const key of keys) {
        result[key] = this.trimForCloudWatch(val[key], depth + 1);
      }
      return result;
    }

    // Over the limit: keep first N keys with real data, summarize the rest
    const result: any = {};
    const kept = keys.slice(0, this.CW_MAX_KEYS);
    for (const key of kept) {
      result[key] = this.trimForCloudWatch(val[key], depth + 1);
    }
    result['__trimmed'] = `${keys.length - this.CW_MAX_KEYS} more keys omitted`;
    return result;
  }

  /**
   *  One-line object summary to avoid recursion and serialization.
   */
  private shallowSummary(obj: any): string {
    const keys = Object.keys(obj);
    const preview = keys.slice(0, 4).join(', ');
    return `{Object(${keys.length} keys: ${preview}${keys.length > 4 ? '…' : ''})}`;
  }

  /**
   * Process log for CloudWatch transmission (only if user is authenticated or in testing mode)
   */
  private async processLogForTransmission(
    level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG',
    args: any[]
  ): Promise<void> {
    // Skip CloudWatch processing during logout
    if (this.isLogoutInProgress) {
      return;
    }

    // Check if WriteLoggingService is in emergency shutdown (use cached module)
    if (!this.writeLoggingServiceChecked) {
      try {
        // Only do this import once
        this.writeLoggingServiceModule = await import('../logging/services/write-logging.service');
        this.writeLoggingServiceChecked = true;
      } catch (error) {
        this.writeLoggingServiceChecked = true;
        // If we can't import, continue without the check
      }
    }
    
    if (this.writeLoggingServiceModule && 
        (this.writeLoggingServiceModule.WriteLoggingService as any).nuclearShutdown) {
      return; // Skip if emergency shutdown is active
    }

    const logId = `${level}-${this.createSafeLogId(args)}-${Date.now()}`;
    
    if (this.processingCloudWatchLogs.has(logId)) {
      return;
    }
    
    this.processingCloudWatchLogs.add(logId);

    try {
      // Emergency circuit breaker - prevent infinite loops (if enabled)
      if (this.duplicateDetectionEnabled) {
        const messageKey = `${level}-${this.createSafeLogId(args)}`;
        if (this.recentLogMessages.has(messageKey)) {
          // Silently skip duplicate log to prevent infinite loops
          return;
        }
        
        // Add to recent messages and clean up after 5 seconds
        this.recentLogMessages.add(messageKey);
        setTimeout(() => {
          this.recentLogMessages.delete(messageKey);
        }, 5000);
      }
      
      let isAuthenticated = false;
      
      // In development mode with CloudWatch testing enabled, bypass authentication check
      const isDevelopmentTesting = this.environment === 'dev' && 
        ((environment as any).enableCloudWatchTesting === true || 
         (environment as any).logging?.enableCloudWatchOnStartup === true);
      
      if (isDevelopmentTesting) {
        isAuthenticated = true;
        // Suppress verbose debug logging to prevent UI blocking
      } else if (this.isLoginInProgress()) {
        isAuthenticated = true;
      } else {
        try {
          isAuthenticated = await Promise.race([
            this.checkUserAuthentication(),
            new Promise<boolean>(resolve => setTimeout(() => resolve(false), 100))
          ]);
        } catch (error) {
          return;
        }
      }
      
      if (!isAuthenticated) {
        // Instead of dropping the log, add it to the AuthFailureQueue with enhanced logging
        const context = this.getContext() || this.getFallbackContext();
        const logRecord = formatLogRecord(level, args, context);
        
        // Apply redaction for security
        if (logRecord.data) {
          logRecord.data = redactAuthenticationData(logRecord.data);
        }
        
        if (typeof logRecord.message === 'string') {
          logRecord.message = redactAuthenticationData(logRecord.message);
        } else if (logRecord.message && typeof logRecord.message === 'object') {
          logRecord.message = redactAuthenticationData(logRecord.message);
        }
        
        // Use enhanced authentication failure handling
        this.handleAuthenticationFailureWithLogging(logRecord);
        
        return;
      }

      const context = this.getContext();
      
      if (!context) {
        if (isDevelopmentTesting) {
          // In development mode, create a minimal context if none exists
          const minimalContext: LogContext = {
            timestamp: new Date().toISOString(),
            sessionId: 'dev-session',
            environment: this.environment,
            service: 'Ground Control',
            thread: 'main',
            externalUserId: 'dev-user'
          };
          
          const logRecord = formatLogRecord(level, args, minimalContext);
          
          // Route the log
          if (level === 'ERROR') {
            this.sendImmediate(logRecord);
          } else {
            this.enqueueLog(logRecord);
          }
          return;
        } else {
          return;
        }
      }

      const logRecord = formatLogRecord(level, args, context);

      // Additional redaction layer for extra security
      if (logRecord.data) {
        logRecord.data = redactAuthenticationData(logRecord.data);
      }
      
      // Smart JWT redaction - redact JWTs in both strings and objects
      if (typeof logRecord.message === 'string') {
        logRecord.message = redactAuthenticationData(logRecord.message);
      } else if (logRecord.message && typeof logRecord.message === 'object') {
        logRecord.message = redactAuthenticationData(logRecord.message);
      }

      // Only route logs if LogQueueManager is available
      if (!this.logQueueManager) {
        if (isDevelopmentTesting) {
          // Try to initialize LogQueueManager
          try {
            await this.initializeLogQueueManager();
            if (!this.logQueueManager) {
              return;
            }
          } catch (error) {
            return;
          }
        } else {
          return;
        }
      }

      if (level === 'ERROR') {
        this.sendImmediate(logRecord);
      } else {
        this.enqueueLog(logRecord);
      }

    } catch (error) {
      // If CloudWatch processing fails, we should enable console fallback, but only do this once 
      if (!this.enableConsoleOutput && this.cloudWatchEnabled) {
        this.enableConsoleFallback();
      }
    } finally {
      setTimeout(() => {
        this.processingCloudWatchLogs.delete(logId);
      }, 1000);
    }
  }

  /**
   * Detect current environment based on Angular environment configuration
   */
  private detectEnvironment(): string {
    // First check Angular environment flags
    if (environment.production) {
      return 'prod';
    }
    
    if (environment.test) {
      return 'test';
    }
    
    if (environment.dev) {
      return 'dev';
    }

    // Fallback to hostname detection if environment flags are not set
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname.toLowerCase();
      
      if (hostname.includes('localhost') || 
          hostname.includes('127.0.0.1') || 
          hostname.includes('dev')) {
        return 'dev';
      }
      
      if (hostname.includes('test') || 
          hostname.includes('staging') || 
          hostname.includes('stage')) {
        return 'test';
      }
      
      return 'prod';
    }
    
    return 'unknown';
  }

  /**
   * Get the appropriate logging configuration based on environment
   */
  private getEnvironmentLoggingConfig(): LoggingConfig {
    let baseConfig: LoggingConfig;
    
    switch (this.environment) {
      case 'prod':
        baseConfig = PRODUCTION_LOGGING_CONFIG;
        break;
      case 'test':
        baseConfig = STAGING_LOGGING_CONFIG;
        break;
      case 'dev':
      default:
        baseConfig = DEVELOPMENT_LOGGING_CONFIG;
        break;
    }

    // Apply validation and environment-specific defaults
    return getEffectiveConfig(baseConfig, this.environment);
  }

  /**
   * Determine if console output should be enabled based on environment
   * Production mode disables console output to improve performance and security
   */
  private shouldEnableConsoleOutput(): boolean {
    // Explicitly disable console output in production and test
    if (this.environment === 'prod' || this.environment === 'test') {
      return false;
    }
    
    // Enable console output only in development environment
    return this.environment === 'dev';
  }

  /**
   * Validate that console configuration matches environment expectations
   * Force disable console output in test/prod if misconfigured
   */
  private validateConsoleConfiguration(): void {
    try {
      // Check if console output is enabled in test or production environments
      if ((this.environment === 'test' || this.environment === 'prod') && this.enableConsoleOutput) {
        // Log warning to CloudWatch (not console) about misconfiguration
        this.logConfigurationWarning(
          `Console output is enabled in ${this.environment} environment - forcing disable for security and performance`,
          {
            environment: this.environment,
            configuredValue: this.loggingConfig.enableConsoleOutput,
            expectedValue: false,
            action: 'force_disable'
          }
        );
        
        // Force disable console output in test/prod
        this.enableConsoleOutput = false;
      } else if (this.environment === 'dev' && !this.enableConsoleOutput) {
        // Log info about console being disabled in development (unusual but valid)
        this.logConfigurationWarning(
          `Console output is disabled in ${this.environment} environment - this is unusual but valid`,
          {
            environment: this.environment,
            configuredValue: this.loggingConfig.enableConsoleOutput,
            expectedValue: true,
            action: 'none'
          }
        );
      }
    } catch (error) {
      // Silent failure to prevent breaking initialization
      // Only log in development environment
      if (this.environment === 'dev') {
        this.originalConsole.error('[LOGGING_SYSTEM] Configuration validation failed:', error);
      }
    }
  }

  /**
   * Log configuration warnings to CloudWatch (not console)
   * This method is used during initialization to report configuration issues
   */
  private logConfigurationWarning(message: string, details?: any): void {
    try {
      // Create a log record for the configuration warning
      const timestamp = new Date().toISOString();
      const warningMessage = `[CONFIG_WARNING] ${timestamp} - ${message}`;
      
      // In test/prod, we can't use console, so we'll queue this for CloudWatch
      // We'll use a deferred approach since CloudWatch may not be initialized yet
      setTimeout(() => {
        try {
          // Try to log via the structured logging API
          if (this.cloudWatchEnabled && this.logQueueManager) {
            this.warn(warningMessage, details);
          } else {
            // If CloudWatch isn't ready, store for later transmission
            // This will be picked up when CloudWatch initializes
            if (this.environment === 'dev') {
              // In development, we can use console for immediate feedback
              this.originalConsole.warn(warningMessage, details);
            }
          }
        } catch (error) {
          // Silent failure - don't break initialization
        }
      }, 1000); // Delay to allow services to initialize
    } catch (error) {
      // Silent failure to prevent breaking initialization
    }
  }



  /**
   * Initialize services with lazy injection to avoid circular dependencies
   */
  private initializeServices(): void {
    setTimeout(() => {
      try {
        try {
          // Try direct injection first (since services have providedIn: 'root')
          import('../logging/services/context.service').then(module => {
            this.contextService = this.injector.get(module.ContextService);
          }).catch(() => {
            // ContextService not available
          });
        } catch (error) {
          // ContextService not available
        }
        
        try {
          // Import LogQueueManager class for proper injection
          import('../logging/services/log-queue-manager.service').then(module => {
            try {
              this.logQueueManager = this.injector.get(module.LogQueueManager);
            } catch (injectionError) {
              // Silent failure
            }
          }).catch((importError) => {
            // Silent failure
          });
        } catch (error) {
          // Silent failure
        }
        
        try {
          import('./amplify.service').then(module => {
            try {
              this.amplifyService = this.injector.get(module.AmplifyService);
            } catch (error) {
              // AmplifyService injection failed (Silent failure)
            }
          }).catch(error => {
            // AmplifyService module not available (Silent failure)
          });
        } catch (error) {
          // Silent failure
        }
        
      } catch (error) {
        // Services not available (Silent failure)
      }
    }, 0);
  }

  /**
   * Initialize router navigation handling to flush logs on route changes
   */
  private initializeRouterHandling(): void {
    setTimeout(() => {
      try {
        // Try to get Router service
        this.router = this.injector.get(Router);
        
        if (this.router) {
          // Subscribe to navigation end events to flush logs on route changes
          this.router.events
            .pipe(
              filter(event => event instanceof NavigationEnd),
              takeUntil(this.destroy$)
            )
            .subscribe((event) => {
              if (this.routeFlushEnabled && event instanceof NavigationEnd) {
                this.internalLog('log', `Route change detected: ${event.url} - flushing logs`);
                this.flushOnRouteChange();
              }
            });
          
          this.internalLog('log', 'Router navigation handling initialized');
        }
      } catch (error) {
        // Router not available or injection failed - silent failure
        this.internalLog('warn', 'Router service not available for navigation handling');
      }
    }, 100); // Slight delay to ensure router is available
  }

  /**
   * Flush logs when route changes occur
   * Uses a lighter flush approach than full page unload
   */
  private flushOnRouteChange(): void {
    try {
      // Flush AuthFailureQueue logs first
      if (!this.authFailureQueue.isEmpty()) {
        const queuedLogs = this.authFailureQueue.flush();
        this.internalLog('log', `Route change - flushing ${queuedLogs.length} queued auth failure logs`);
        
        // Add queued logs to the regular queue for transmission
        for (const logRecord of queuedLogs) {
          try {
            this.enqueueLog(logRecord);
          } catch (error) {
            // Silent failure during route change
          }
        }
      }

      // Flush regular logs via LogQueueManager
      if (this.logQueueManager && typeof this.logQueueManager.flush === 'function') {
        this.logQueueManager.flush().catch((error: any) => {
          // Silent failure during route change
          this.internalLog('warn', 'Error flushing logs during route change:', error);
        });
      }
    } catch (error) {
      // Silent failure to prevent disrupting navigation
      this.internalLog('warn', 'Error during route change log flush:', error);
    }
  }

  /**
   * Enable or disable route-based log flushing
   * @param enabled - Whether to flush logs on route changes
   */
  setRouteFlushEnabled(enabled: boolean): void {
    this.routeFlushEnabled = enabled;
    this.internalLog('log', `Route-based log flushing ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if route-based log flushing is enabled
   * @returns True if route flushing is enabled
   */
  isRouteFlushEnabled(): boolean {
    return this.routeFlushEnabled;
  }

  /**
   * Get current context from ContextService
   */
  private getContext(): LogContext | null {
    try {
      if (this.contextService && typeof this.contextService.getContext === 'function') {
        return this.contextService.getContext();
      }
      
      return this.getFallbackContext();
      
    } catch (error) {
      return this.getFallbackContext();
    }
  }

  /**
   * Get fallback context when ContextService is unavailable
   */
  private getFallbackContext(): LogContext {
    return {
      timestamp: new Date().toISOString(),
      sessionId: 'fallback-session',
      environment: this.environment,
      service: 'Ground Control',
      thread: 'main',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined
    };
  }

  /**
   * Send ERROR logs immediately via LogQueueManager
   */
  private sendImmediate(logRecord: LogRecord): void {
    try {
      if (this.logQueueManager && typeof this.logQueueManager.sendImmediate === 'function') {
        this.logQueueManager.sendImmediate(logRecord);
      }
    } catch (error) {
      // Silent failure to prevent loops
    }
  }

  /**
   * Enqueue logs for batched transmission via LogQueueManager
   */
  private enqueueLog(logRecord: LogRecord): void {
    try {
      if (this.logQueueManager && typeof this.logQueueManager.enqueue === 'function') {
        this.logQueueManager.enqueue(logRecord);
      }
    } catch (error) {
      // Silent failure to prevent loops
    }
  }

  /**
   * Handle authentication restoration by flushing queued logs
   * This method should be called when authentication is restored
   */
  async onAuthenticationRestored(): Promise<void> {
    if (this.authFailureQueue.isEmpty()) {
      this.logInternalOperation('Authentication restored but queue is empty');
      return;
    }

    try {
      const queuedLogs = this.authFailureQueue.flush();
      this.errorMetrics.totalFlushedLogs += queuedLogs.length;
      
      this.logInternalOperation('Authentication restored - flushing queued logs', {
        queuedLogsCount: queuedLogs.length,
        totalFlushedLogs: this.errorMetrics.totalFlushedLogs
      });

      let successCount = 0;
      let errorCount = 0;

      // Send queued logs to CloudWatch with error tracking
      for (const logRecord of queuedLogs) {
        try {
          // Route the log based on its level
          if (logRecord.level === 'ERROR') {
            this.sendImmediate(logRecord);
          } else {
            this.enqueueLog(logRecord);
          }
          successCount++;
        } catch (error) {
          errorCount++;
          this.errorMetrics.queueOperationErrors++;
          
          // If individual log fails, continue with others
          this.logInternalOperation('Failed to send queued log', {
            error: this.getErrorMessage(error),
            logLevel: logRecord.level,
            logPreview: typeof logRecord.message === 'string' ? logRecord.message.substring(0, 50) : 'object'
          }, 'warn');
        }
      }

      this.logInternalOperation('Queue flush completed', {
        totalLogs: queuedLogs.length,
        successCount,
        errorCount,
        successRate: queuedLogs.length > 0 ? (successCount / queuedLogs.length * 100).toFixed(1) + '%' : '0%'
      });

    } catch (error) {
      this.errorMetrics.queueOperationErrors++;
      this.errorMetrics.lastErrorTimestamp = new Date().toISOString();
      
      this.logInternalOperation('Authentication restoration flush failed', {
        error: this.getErrorMessage(error),
        queueSize: this.authFailureQueue.size()
      }, 'error');

      // Attempt recovery
      this.recoverFromQueueError();
    }
  }

  /**
   * Public method to notify the logging service that authentication has been restored
   * This should be called by authentication services after successful login
   */
  async notifyAuthenticationRestored(): Promise<void> {
    this.logInternalOperation('Authentication restoration notification received', {
      queueSize: this.authFailureQueue.size(),
      cloudWatchEnabled: this.cloudWatchEnabled
    });
    
    // Clear the authentication cache to force a fresh check
    this.authCheckCache = null;
    
    // Enable CloudWatch logging if not already enabled
    if (!this.cloudWatchEnabled) {
      await this.enableCloudWatchLoggingAsync();
    } else {
      // Just flush the queue if CloudWatch is already enabled
      await this.onAuthenticationRestored();
    }
  }

  /**
   * Get AuthFailureQueue statistics for monitoring
   */
  getAuthFailureQueueStats(): {
    currentSize: number;
    maxSize: number;
    isEmpty: boolean;
    isFull: boolean;
    oldestEntryAge?: number;
    newestEntryAge?: number;
  } {
    return this.authFailureQueue.getStatistics();
  }

  /**
   * Clear the authentication failure queue
   * This should be called during logout to prevent data leakage
   */
  clearAuthFailureQueue(): void {
    const clearedCount = this.authFailureQueue.size();
    this.authFailureQueue.clear();
    this.internalLog('log', `Cleared ${clearedCount} logs from authentication failure queue`);
  }

  /**
   * Set logout state to track when logout is in progress
   * This should be called by logout process to signal logging service
   * @param inProgress - true when logout starts, false when complete
   */
  setLogoutInProgress(inProgress: boolean): void {
    this.isLogoutInProgress = inProgress;
    
    if (inProgress) {
      // Clear authentication failure queue on logout initiation
      this.clearAuthFailureQueue();
      
      // Suppress 401 error modals during logout
      this.suppressAuthErrors = true;
      
      // Disable CloudWatch transmission attempts during logout process
      this.cloudWatchEnabled = false;
      
      this.internalLog('log', 'Logout initiated - auth errors suppressed, CloudWatch disabled, queue cleared');
    } else {
      // Re-enable error handling after logout is complete
      this.suppressAuthErrors = false;
      
      this.internalLog('log', 'Logout completed - auth error suppression disabled');
    }
  }

  /**
   * Check if logout is currently in progress
   */
  isLogoutInProgressState(): boolean {
    return this.isLogoutInProgress;
  }

  /**
   * Check if authentication errors should be suppressed
   * This is used to prevent 401 modals during logout
   */
  shouldSuppressAuthErrors(): boolean {
    return this.suppressAuthErrors;
  }

  /**
   * Manually flush authentication failure queue (for debugging)
   */
  flushAuthFailureQueue(): void {
    this.onAuthenticationRestored().catch(error => {
      this.internalLog('error', `Failed to flush auth failure queue: ${error}`);
    });
  }

  /**
   * Enable CloudWatch logging only if user is authenticated
   */
  async enableCloudWatchLogging(): Promise<void> {
    const isAuthenticated = await this.checkUserAuthentication();
    
    if (!isAuthenticated) {
      return;
    }

    this.cloudWatchEnabled = true;
    
    try {
      if (this.logQueueManager && typeof this.logQueueManager.enableCloudWatch === 'function') {
        this.logQueueManager.enableCloudWatch();
      }
    } catch (error) {
      // Silent failure
    }

    // Flush any queued logs from authentication failures
    await this.onAuthenticationRestored();

    if (!environment.production) {
      setTimeout(() => {
        this.info('CloudWatch logging enabled', {
          environment: this.environment,
          timestamp: new Date().toISOString(),
          service: 'Ground Control'
        });
      }, 100);
    }
  }

  /**
   * Enable CloudWatch logging asynchronously without blocking authentication
   */
  enableCloudWatchLoggingAsync(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(async () => {
        try {
          if (this.isLoginInProgress()) {
            this.cloudWatchEnabled = true;
            
            try {
              if (this.logQueueManager && typeof this.logQueueManager.enableCloudWatch === 'function') {
                this.logQueueManager.enableCloudWatch();
              }
            } catch (error) {
              // Silent failure
            }
            
            resolve();
            return;
          }

          const isAuthenticated = await Promise.race([
            this.checkUserAuthentication(),
            new Promise<boolean>(resolve => setTimeout(() => resolve(false), 1000))
          ]);
          
          if (isAuthenticated) {
            this.cloudWatchEnabled = true;
            
            try {
              if (this.logQueueManager && typeof this.logQueueManager.enableCloudWatch === 'function') {
                this.logQueueManager.enableCloudWatch();
              }
            } catch (error) {
              // Silent failure
            }

            // Flush any queued logs from authentication failures
            await this.onAuthenticationRestored();
          }
          
          resolve();
        } catch (error) {
          resolve();
        }
      }, 0);
    });
  }

  /**
   * Disable CloudWatch logging
   */
  disableCloudWatchLogging(): void {
    this.cloudWatchEnabled = false;
    
    try {
      if (this.logQueueManager && typeof this.logQueueManager.disableCloudWatch === 'function') {
        this.logQueueManager.disableCloudWatch();
      }
    } catch (error) {
      // Silent failure
    }
  }

  /**
   * Public API method for structured info logging
   */
  info(message: string, data?: any): void {
    this.logStructured('INFO', message, data);
  }

  /**
   * Public API method for structured warning logging
   */
  warn(message: string, data?: any): void {
    this.logStructured('WARN', message, data);
  }

  /**
   * Public API method for structured error logging
   */
  error(message: string, data?: any): void {
    this.logStructured('ERROR', message, data);
  }

  /**
   * Public API method for structured debug logging
   */
  debug(message: string, data?: any): void {
    this.logStructured('DEBUG', message, data);
  }

  /**
   * Structured logging entry point. Same protections as the console bridge.
   */
  private logStructured(
    level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG',
    message: string,
    data?: any
  ): void {
    if ((window as any).loggingDisabled) {
      if (this.enableConsoleOutput) {
        const consoleMethod = this.getOriginalConsoleMethod(level);
        const safeData = data !== undefined ? this.safeArgs([data])[0] : undefined;
        safeData !== undefined ? consoleMethod(message, safeData) : consoleMethod(message);
      }
      return;
    }

    if (this.isProcessingLog) {
      return;
    }

    if (this.isLoginInProgress()) {
      if (this.enableConsoleOutput && !this.shouldAutoSuppress()) {
        const consoleMethod = this.getOriginalConsoleMethod(level);
        const safeData = data !== undefined ? this.safeArgs([data])[0] : undefined;
        safeData !== undefined ? consoleMethod(message, safeData) : consoleMethod(message);
      }
      return;
    }

    try {
      this.isProcessingLog = true;

      // Console output
      if (this.enableConsoleOutput && !this.shouldAutoSuppress()) {
        try {
          const consoleMethod = this.getOriginalConsoleMethod(level);
          const safeData = data !== undefined ? this.safeArgs([data])[0] : undefined;
          safeData !== undefined ? consoleMethod(message, safeData) : consoleMethod(message);
        } catch (_) { /* never block */ }
      }

      // CloudWatch
      if (this.cloudWatchEnabled) {
        const args = data !== undefined ? [message, data] : [message];
        this.scheduleCloudWatch(level, args);
      }

    } catch (error) {
      // Silent failure
    } finally {
      this.isProcessingLog = false;
    }
  }



  /**
   * Fallback implementation for console.table when not available
   */
  private fallbackTable(data: any): void {
    // Only output in development environment
    if (this.environment === 'dev') {
      if (typeof data === 'object' && data !== null) {
        this.originalConsole.log('Table data:', JSON.stringify(data, null, 2));
      } else {
        this.originalConsole.log('Table data:', data);
      }
    }
  }

  /**
   * Safe table method that uses console.table if available, otherwise falls back
   */
  private safeTable(data: any): void {
    // Only output in development environment
    if (this.environment === 'dev') {
      if (this.originalConsole.table) {
        this.originalConsole.table(data);
      } else {
        this.fallbackTable(data);
      }
    }
  }

  /**
   * Get original console method for fallback
   */
  private getOriginalConsoleMethod(level: string): (...args: any[]) => void {
    switch (level.toLowerCase()) {
      case 'error':
        return this.originalConsole.error;
      case 'warn':
        return this.originalConsole.warn;
      case 'info':
        return this.originalConsole.info;
      case 'debug':
        return this.originalConsole.debug;
      default:
        return this.originalConsole.log;
    }
  }

  /**
   * Get logging metrics from LogQueueManager
   */
  getLoggingMetrics(): LoggingMetrics | null {
    try {
      if (this.logQueueManager && typeof this.logQueueManager.getMetrics === 'function') {
        return this.logQueueManager.getMetrics();
      }
      
      return {
        queueSize: 0,
        failedTransmissions: 0,
        droppedLogs: 0,
        totalProcessed: 0,
        cloudWatchEnabled: this.cloudWatchEnabled
      };
      
    } catch (error) {
      return null;
    }
  }

  /**
   * Flush remaining logs (call on app shutdown)
   */
  flush(): void {
    try {
      if (this.logQueueManager && typeof this.logQueueManager.flush === 'function') {
        this.logQueueManager.flush();
      }
    } catch (error) {
      // Silent failure
    }
  }

  /**
   * Flush logs using sendBeacon API for reliable page unload transmission
   * Includes both regular queued logs and AuthFailureQueue logs
   */
  flushWithBeacon(): void {
    try {
      // First, flush any queued authentication failure logs
      if (!this.authFailureQueue.isEmpty()) {
        const queuedLogs = this.authFailureQueue.flush();
        this.internalLog('log', `Page unload - flushing ${queuedLogs.length} queued auth failure logs`);
        
        // Add queued logs to the regular queue for beacon transmission
        for (const logRecord of queuedLogs) {
          try {
            this.enqueueLog(logRecord);
          } catch (error) {
            // Silent failure during page unload
          }
        }
      }

      // Flush regular logs using LogQueueManager's beacon implementation
      if (this.logQueueManager && typeof this.logQueueManager.flushWithBeacon === 'function') {
        this.logQueueManager.flushWithBeacon();
      } else {
        // Fallback: if LogQueueManager's flushWithBeacon is not available,
        // attempt direct beacon transmission of AuthFailureQueue logs
        this.fallbackBeaconFlush();
      }
    } catch (error) {
      // Silent failure during page unload
      this.internalLog('warn', 'Error during beacon flush:', error);
    }
  }

  /**
   * Fallback beacon flush when LogQueueManager's flushWithBeacon is not available
   * Attempts to send AuthFailureQueue logs directly using sendBeacon API
   */
  private fallbackBeaconFlush(): void {
    try {
      // If we have any remaining logs in the AuthFailureQueue, try direct beacon transmission
      if (!this.authFailureQueue.isEmpty()) {
        const remainingLogs = this.authFailureQueue.flush();
        this.internalLog('log', `Fallback beacon flush - attempting direct transmission of ${remainingLogs.length} logs`);
        
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          this.sendLogsViaBeaconFallback(remainingLogs);
        } else {
          // No beacon API available, fall back to console logging
          this.fallbackToConsoleLogging(remainingLogs);
        }
      }
      
      // Also try regular flush as final fallback
      this.flush();
    } catch (error) {
      this.internalLog('warn', 'Error during fallback beacon flush:', error);
    }
  }

  /**
   * Send logs directly via sendBeacon API as fallback
   * Uses simplified transmission format when LogQueueManager is unavailable
   */
  private sendLogsViaBeaconFallback(logs: LogRecord[]): void {
    try {
      // Get CloudWatch endpoint (simplified version)
      const endpoint = this.getBeaconEndpointFallback();
      
      if (!endpoint) {
        this.fallbackToConsoleLogging(logs);
        return;
      }

      let successCount = 0;
      let failureCount = 0;
      const failedLogs: LogRecord[] = [];

      // Send each log individually via beacon
      logs.forEach(logRecord => {
        try {
          const payload = JSON.stringify({
            logStream: logRecord.kinective.sessionId,
            logGroupName: this.getLogGroupNameFallback(),
            log: logRecord
          });

          const success = navigator.sendBeacon(endpoint, payload);
          
          if (success) {
            successCount++;
          } else {
            failureCount++;
            failedLogs.push(logRecord);
          }
        } catch (error) {
          failureCount++;
          failedLogs.push(logRecord);
        }
      });

      this.internalLog('log', `Beacon fallback transmission: ${successCount} successful, ${failureCount} failed`);

      // Fall back to console logging for failed logs
      if (failedLogs.length > 0) {
        this.fallbackToConsoleLogging(failedLogs);
      }
    } catch (error) {
      this.internalLog('warn', 'Error in beacon fallback transmission:', error);
      this.fallbackToConsoleLogging(logs);
    }
  }

  /**
   * Get CloudWatch endpoint for beacon transmission (fallback implementation)
   */
  private getBeaconEndpointFallback(): string | null {
    try {
      // Use the same logic as LogQueueManager but simplified
      if (typeof Buffer !== 'undefined') {
        const Buffer = require('buffer').Buffer;
        const baseUrl = Buffer.from(environment.environmentURL, 'base64').toString();
        
        if (baseUrl) {
          return `${baseUrl}${CLOUDWATCH_ENDPOINTS.SINGLE_LOG}`;
        }
      }
      return null;
    } catch (error) {
      this.internalLog('warn', 'Error getting beacon endpoint:', error);
      return null;
    }
  }

  /**
   * Get log group name for beacon transmission (fallback implementation)
   */
  private getLogGroupNameFallback(): string {
    if (environment.production) {
      return LOG_GROUP_NAMES.PRODUCTION;
    } else if (environment.test) {
      return LOG_GROUP_NAMES.TEST;
    } else {
      return LOG_GROUP_NAMES.DEVELOPMENT;
    }
  }

  /**
   * Fallback to console logging when beacon transmission fails
   */
  private fallbackToConsoleLogging(logs: LogRecord[]): void {
    logs.forEach(logRecord => {
      const level = logRecord.level.toLowerCase();
      const consoleMethod = (console as any)[level] || console.log;
      consoleMethod(`[UNLOAD] [${logRecord.timestamp}] ${logRecord.level}: ${logRecord.message}`, logRecord.data);
    });
    
    this.internalLog('log', `${logs.length} logs sent to console as final fallback during page unload`);
  }

  /**
   * Check if dependent services are available
   */
  private areServicesAvailable(): boolean {
    return !!(this.contextService && this.logQueueManager);
  }

  /**
   * Handle graceful degradation when services are unavailable
   */
  private handleServiceUnavailable(level: string, message: string, data?: any): void {
    const consoleMethod = this.getOriginalConsoleMethod(level);
    
    if (data !== undefined) {
      consoleMethod(`[${level}] ${message}`, data);
    } else {
      consoleMethod(`[${level}] ${message}`);
    }
  }

  /**
   * Get current CloudWatch logging state
   */
  isCloudWatchEnabled(): boolean {
    return this.cloudWatchEnabled;
  }

  /**
   * Get current environment
   */
  getEnvironment(): string {
    return this.environment;
  }

  /**
   * Check if console output is enabled
   */
  isConsoleOutputEnabled(): boolean {
    return this.enableConsoleOutput;
  }

  /**
   * Manual test method to trigger CloudWatch logging (for debugging)
   */
  async testCloudWatchLogging(): Promise<void> {
    this.internalLog('log', 'Testing CloudWatch logging...');
    
    this.cloudWatchEnabled = true;
    
    if (this.logQueueManager && typeof this.logQueueManager.enableCloudWatch === 'function') {
      this.logQueueManager.enableCloudWatch();
    }
    
    this.info('Test CloudWatch log message', {
      testData: 'This is a test log for CloudWatch',
      timestamp: new Date().toISOString(),
      environment: this.environment
    });
    
    this.internalLog('log', 'Test log sent. Check CloudWatch metrics and network requests.');
  }

  /**
   * Force enable CloudWatch logging without authentication check (for testing only)
   */
  forceEnableCloudWatch(): void {
    this.internalLog('log', 'Force enabling CloudWatch logging...');
    
    this.cloudWatchEnabled = true;
    
    if (this.logQueueManager && typeof this.logQueueManager.enableCloudWatch === 'function') {
      this.logQueueManager.enableCloudWatch();
    }
    
    this.internalLog('log', 'CloudWatch logging force enabled.');
  }

  /**
   * Clear authentication cache (for testing)
   */
  clearAuthCache(): void {
    this.authCheckCache = null;
    this.internalLog('log', 'Authentication cache cleared');
  }

  /**
   * Clear CloudWatch processing set (for testing)
   */
  clearProcessingSet(): void {
    this.processingCloudWatchLogs.clear();
    this.internalLog('log', 'CloudWatch processing set cleared');
  }

  /**
   * Signal that login is in progress (prevents auth checks from blocking)
   */
  setLoginInProgress(inProgress: boolean): void {
    (window as any).loginInProgress = inProgress;
    
    if (inProgress) {
      this.cloudWatchEnabled = false;
      // Remove console log to prevent infinite loops
    } else {
      // Remove console log to prevent infinite loops
    }
  }

  /**
   * Check if login is currently in progress
   */
  private isLoginInProgress(): boolean {
    return !!(window as any).loginInProgress || 
           (typeof location !== 'undefined' && location.href.includes('login')) ||
           (typeof location !== 'undefined' && location.href.includes('auth')) ||
           this.isLogoutInProgress;
  }

  /**
   * Emergency kill switch - completely disable all logging processing
   */
  disableAllLogging(): void {
    this.cloudWatchEnabled = false;
    this.isProcessingLog = true;
    (window as any).loggingDisabled = true;
    this.internalLog('warn', 'EMERGENCY: All logging processing disabled');
  }

  /**
   * Force stop all CloudWatch attempts immediately
   */
  forceStopCloudWatch(): void {
    this.cloudWatchEnabled = false;
    if (this.logQueueManager && typeof this.logQueueManager.disableCloudWatch === 'function') {
      this.logQueueManager.disableCloudWatch();
    }
    this.internalLog('warn', 'CloudWatch logging force stopped');
  }

  /**
   * Re-enable logging after emergency disable
   */
  enableAllLogging(): void {
    this.isProcessingLog = false;
    (window as any).loggingDisabled = false;
    this.internalLog('log', 'Logging processing re-enabled');
  }

  /**
   * Create a safe log ID from args without circular references
   */
  private createSafeLogId(args: any[]): string {
    try {
      const safeArgs = args.map(arg => {
        if (typeof arg === 'string') {
          return arg.substring(0, 50);
        } else if (typeof arg === 'number' || typeof arg === 'boolean') {
          return String(arg);
        } else if (arg && typeof arg === 'object') {
          const type = arg.constructor?.name || 'Object';
          const keys = Object.keys(arg).slice(0, 3).join(',');
          return `${type}[${keys}]`;
        }
        return typeof arg;
      });
      
      return safeArgs.join('-').substring(0, 100);
    } catch (error) {
      return `fallback-${Date.now()}`;
    }
  }

  /**
   * Get current logging status for debugging
   */
  getLoggingStatus(): any {
    return {
      cloudWatchEnabled: this.cloudWatchEnabled,
      environment: this.environment,
      consoleOutputEnabled: this.enableConsoleOutput,
      logQueueManagerAvailable: !!(this.logQueueManager),
      contextServiceAvailable: !!(this.contextService),
      amplifyServiceAvailable: !!(this.amplifyService),
      logQueueManagerMethods: this.logQueueManager ? Object.getOwnPropertyNames(Object.getPrototypeOf(this.logQueueManager)) : [],
      logQueueManagerType: this.logQueueManager ? this.logQueueManager.constructor.name : 'null',
      servicesInitialized: this.areServicesInitialized(),
      authFailureQueue: this.getAuthFailureQueueStats(),
      metrics: this.getLoggingMetrics(),
      isLogoutInProgress: this.isLogoutInProgress,
      suppressAuthErrors: this.suppressAuthErrors
    };
  }

  /**
   * Check if all required services are initialized
   */
  private areServicesInitialized(): boolean {
    return !!(this.logQueueManager && this.contextService);
  }

  /**
   * Force retry service initialization (for debugging)
   */
  retryServiceInitialization(): void {
    this.internalLog('log', 'Retrying service initialization...');
    this.initializeServices();
    
    setTimeout(() => {
      const status = this.getLoggingStatus();
      this.internalLog('log', 'Service initialization status:', status);
    }, 1500);
  }

  /**
   * Test logging functionality (TEMPORARY)
   */
  testLogging(): void {
    this.internalLog('log', 'Testing logging functionality...');
    
    // Test direct logging methods
    this.info('Test INFO log', { test: 'data', timestamp: new Date().toISOString() });
    this.warn('Test WARN log', { test: 'warning' });
    this.error('Test ERROR log', { test: 'error' });
    
    // Test console methods (if bridged) - use original console to avoid loops - only in dev
    if (this.environment === 'dev') {
      this.originalConsole.log('Test console.log');
      this.originalConsole.info('Test console.info');
    }
    
    this.internalLog('log', 'Test logs sent. Check console for debug output.');
  }

  /**
   * Test the complete log flow from console to CloudWatch
   */
  testLogFlow(): void {
    this.internalLog('log', '=== Testing Complete Log Flow ===');
    
    // Test console.log (should be intercepted)
    console.log('TEST: This should go to CloudWatch via console interception');
    
    // Test direct logging methods
    this.info('TEST: Direct info log', { testData: 'should go to CloudWatch' });
    this.warn('TEST: Direct warn log', { testData: 'should go to CloudWatch' });
    
    // Test error (should be sent immediately)
    this.error('TEST: Direct error log', { testData: 'should go to CloudWatch immediately' });
    
    this.internalLog('log', '=== Log Flow Test Complete - Check CloudWatch ===');
  }

  /**
   * Get queue status for debugging missing logs
   */
  getQueueStatus(): any {
    const loggingStatus = this.getLoggingStatus();
    
    let queueManagerStatus = null;
    if (this.logQueueManager) {
      try {
        queueManagerStatus = {
          metrics: this.logQueueManager.getMetrics(),
          detailedMetrics: this.logQueueManager.getDetailedMetrics ? this.logQueueManager.getDetailedMetrics() : null
        };
      } catch (error) {
        queueManagerStatus = { error: 'Failed to get queue manager status' };
      }
    }
    
    const status = {
      timestamp: new Date().toISOString(),
      loggingService: loggingStatus,
      queueManager: queueManagerStatus,
      environment: this.environment,
      cloudWatchEnabled: this.cloudWatchEnabled,
      consoleOutputEnabled: this.enableConsoleOutput
    };
    
    this.internalLog('log', 'Queue Status Report:', status);
    return status;
  }

  /**
   * Force flush the log queue for debugging
   */
  forceFlushQueue(): void {
    this.internalLog('log', 'Force flushing log queue...');
    
    if (this.logQueueManager && typeof this.logQueueManager.flush === 'function') {
      this.logQueueManager.flush().then(() => {
        this.internalLog('log', 'Queue flush completed');
      }).catch((error: any) => {
        this.internalLog('error', 'Queue flush failed:', error);
      });
    } else {
      this.internalLog('warn', 'LogQueueManager flush not available');
    }
  }

  /**
   * Disable duplicate detection for debugging
   */
  disableDuplicateDetection(): void {
    this.duplicateDetectionEnabled = false;
    this.recentLogMessages.clear();
    this.internalLog('log', 'Duplicate detection disabled - all logs will be processed');
  }

  /**
   * Enable duplicate detection
   */
  enableDuplicateDetection(): void {
    this.duplicateDetectionEnabled = true;
    this.internalLog('log', 'Duplicate detection enabled');
  }

  /**
   * Test CloudWatch vs Console behavior
   */
  testCloudWatchVsConsole(): void {
    this.internalLog('log', '=== Testing CloudWatch vs Console Behavior ===');
    
    const status = this.getConsoleStatus();
    this.internalLog('log', 'Current status:', status);
    
    if (this.cloudWatchEnabled) {
      this.internalLog('log', 'CloudWatch is enabled - console output should be minimal');
      this.internalLog('log', 'Testing structured logging (should go to CloudWatch only):');
      this.info('CloudWatch test message', { cloudWatch: true, timestamp: new Date().toISOString() });
      
      this.internalLog('log', 'Testing console.log (should be intercepted and sent to CloudWatch):');
      console.log('This console.log should go to CloudWatch, not console');
      
    } else {
      this.internalLog('log', 'CloudWatch is disabled - console output should be visible');
      this.info('Console fallback test message', { console: true, timestamp: new Date().toISOString() });
      console.log('This console.log should appear in console');
    }
    
    this.internalLog('log', '=== Test Complete ===');
  }

  /**
   * Internal logging method that bypasses the logging system to prevent infinite loops
   * Only outputs in development environment
   */
  private internalLog(level: 'log' | 'warn' | 'error' | 'debug', message: string, data?: any): void {
    try {
      // Only output internal logs in development environment
      if (this.environment !== 'dev') {
        return;
      }
      
      const taggedMessage = `${this.LOGGING_SYSTEM_TAG} ${message}`;
      if (data) {
        this.originalConsole[level === 'log' ? 'log' : level](taggedMessage, data);
      } else {
        this.originalConsole[level === 'log' ? 'log' : level](taggedMessage);
      }
    } catch (error) {
      // Silent failure - don't break the logging system
    }
  }

  /**
   * Enable console fallback when CloudWatch fails
   * Only enables console output in development environment
   */
  enableConsoleFallback(): void {
    // Only enable console fallback in development
    if (this.environment === 'dev') {
      this.enableConsoleOutput = true;
      this.internalLog('log', 'Console fallback enabled due to CloudWatch failure');
    } else {
      // In test/prod, log the error but keep console disabled
      this.internalLog('error', 'CloudWatch failure detected in ' + this.environment + ' environment - console remains disabled');
    }
  }

  /**
   * Disable console fallback when CloudWatch is working
   * Restores the original configuration value and respects environment-specific configuration
   */
  disableConsoleFallback(): void {
    if (this.cloudWatchEnabled) {
      // Restore original configuration value
      this.enableConsoleOutput = this.loggingConfig.enableConsoleOutput ?? this.shouldEnableConsoleOutput();
      this.internalLog('log', 'Console fallback disabled - CloudWatch is working');
    }
  }

  /**
   * Get console status for debugging
   */
  getConsoleStatus(): any {
    return {
      consoleOutputEnabled: this.enableConsoleOutput,
      cloudWatchEnabled: this.cloudWatchEnabled,
      environment: this.environment,
      isLoginInProgress: this.isLoginInProgress(),
      isProcessingLog: this.isProcessingLog,
      consoleBridged: LoggingService.consoleBridged
    };
  }

  /**
   * Get circuit breaker status from WriteLoggingService
   */
  getCircuitBreakerStatus(): any {
    try {
      if (this.logQueueManager && this.logQueueManager.writeLoggingService && 
          typeof this.logQueueManager.writeLoggingService.getCircuitBreakerStatus === 'function') {
        return this.logQueueManager.writeLoggingService.getCircuitBreakerStatus();
      } else {
        this.internalLog('warn', 'WriteLoggingService circuit breaker not available');
        return { error: 'Circuit breaker status not available' };
      }
    } catch (error) {
      this.internalLog('error', 'Error getting circuit breaker status: ' + error);
      return { error: 'Failed to get circuit breaker status' };
    }
  }

  /**
   * Restore original console methods to break infinite loops
   */
  restoreOriginalConsole(): void {
    try {
      console.log = this.originalConsole.log;
      console.info = this.originalConsole.info;
      console.warn = this.originalConsole.warn;
      console.error = this.originalConsole.error;
      console.debug = this.originalConsole.debug;
      
      LoggingService.consoleBridged = false;
      
      // Use original console to report success - only in dev
      if (this.environment === 'dev') {
        this.originalConsole.log('[EMERGENCY] Original console methods restored');
      }
    } catch (error) {
      // Silent failure
    }
  }

  /**
   * Completely disable all logging to break infinite loops
   */
  emergencyDisableLogging(): void {
    try {
      // Disable all logging flags
      this.cloudWatchEnabled = false;
      this.enableConsoleOutput = false;
      this.isProcessingLog = true;
      (window as any).loggingDisabled = true;
      
      // Restore original console
      this.restoreOriginalConsole();
      
      // Use original console to report - only in dev
      if (this.environment === 'dev') {
        this.originalConsole.error('[EMERGENCY] All logging disabled and console restored');
      }
    } catch (error) {
      // Silent failure
    }
  }

  /**
   * Start periodic authentication retry mechanism
   * Checks authentication every 30 seconds and flushes queued logs when restored
   */
  private startAuthenticationRetryMechanism(): void {
    // Clear any existing interval
    if (this.authRetryInterval) {
      clearInterval(this.authRetryInterval);
    }

    this.authRetryInterval = setInterval(async () => {
      try {
        // Skip retry during login process
        if (this.isLoginInProgress()) {
          return;
        }

        // Skip if queue is empty
        if (this.authFailureQueue.isEmpty()) {
          return;
        }

        // Check if authentication is restored
        const wasAuthenticated = this.authCheckCache?.result || false;
        const isNowAuthenticated = await this.checkUserAuthentication();

        // If authentication was restored, flush the queue
        if (!wasAuthenticated && isNowAuthenticated) {
          this.internalLog('log', 'Authentication restored via periodic check - flushing queued logs');
          await this.onAuthenticationRestored();
        }
      } catch (error) {
        // Silent failure to prevent disrupting the retry mechanism
        this.internalLog('warn', `Authentication retry check failed: ${error}`);
      }
    }, this.AUTH_RETRY_INTERVAL_MS);

    this.internalLog('log', `Authentication retry mechanism started (interval: ${this.AUTH_RETRY_INTERVAL_MS}ms)`);
  }

  /**
   * Stop the periodic authentication retry mechanism
   */
  private stopAuthenticationRetryMechanism(): void {
    if (this.authRetryInterval) {
      clearInterval(this.authRetryInterval);
      this.authRetryInterval = null;
      this.internalLog('log', 'Authentication retry mechanism stopped');
    }
  }

  /**
   * Check if user is authenticated using AmplifyService (non-blocking)
   */
  private async checkUserAuthentication(): Promise<boolean> {
    if (this.isLoginInProgress()) {
      return true;
    }

    const now = Date.now();
    const previousAuthState = this.authCheckCache?.result || false;
    
    if (this.authCheckCache && (now - this.authCheckCache.timestamp) < this.AUTH_CACHE_DURATION) {
      return this.authCheckCache.result;
    }

    // For failed authentication, use shorter cache duration to retry sooner
    if (this.authCheckCache && !this.authCheckCache.result && (now - this.authCheckCache.timestamp) < 5000) {
      return false;
    }

    try {
      // Use cached amplifyService if available, otherwise try to get it once
      if (!this.amplifyService) {
        try {
          // Try to get from injector first (faster than dynamic import)
          const AmplifyService = (await import('./amplify.service')).AmplifyService;
          this.amplifyService = this.injector.get(AmplifyService, null);
        } catch (error) {
          this.internalLog('warn', 'AmplifyService not available for authentication check');
          this.authCheckCache = { result: false, timestamp: now };
          return false;
        }
      }

      if (!this.amplifyService || typeof this.amplifyService.getCurrentUser !== 'function') {
        this.internalLog('warn', 'AmplifyService getCurrentUser method not available');
        this.authCheckCache = { result: false, timestamp: now };
        return false;
      }

      // Use a shorter timeout to avoid blocking
      const userResult = await Promise.race([
        this.amplifyService.getCurrentUser(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Auth check timeout')), 300))
      ]);
      const isAuthenticated = !!(userResult && userResult.success);
      
      // Log authentication state changes with enhanced logging
      if (this.authCheckCache && this.authCheckCache.result !== isAuthenticated) {
        this.logAuthenticationStateChange(previousAuthState, isAuthenticated, 'periodic_check');
        
        if (isAuthenticated) {
          this.internalLog('log', 'Authentication restored - user is now authenticated');
        } else {
          this.internalLog('warn', 'Authentication lost - user is no longer authenticated');
        }
      }
      
      this.authCheckCache = { result: isAuthenticated, timestamp: now };
      
      return isAuthenticated;

    } catch (error) {
      // Log authentication check errors
      this.errorMetrics.authenticationStateErrors++;
      this.errorMetrics.lastErrorTimestamp = new Date().toISOString();
      
      this.logInternalOperation('Authentication check failed', {
        error: this.getErrorMessage(error),
        previousState: previousAuthState
      }, 'warn');
      
      this.authCheckCache = { result: false, timestamp: now };
      return false;
    }
  }

  /**
   * Cleanup method to stop intervals and clear resources
   */
  cleanup(): void {
    this.stopAuthenticationRetryMechanism();
    
    if (this.reentrancyTimeout) {
      clearTimeout(this.reentrancyTimeout);
      this.reentrancyTimeout = null;
    }
    
    this.internalLog('log', 'LoggingService cleanup completed');
  }

  /**
   * Initialize LogQueueManager for development mode
   */
  private async initializeLogQueueManager(): Promise<void> {
    try {
      const module = await import('../logging/services/log-queue-manager.service');
      this.logQueueManager = this.injector.get(module.LogQueueManager);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check current logging status for debugging
   */
  checkLoggingStatus(): any {
    const isDevelopmentTesting = this.environment === 'dev' && 
      ((environment as any).enableCloudWatchTesting === true || 
       (environment as any).logging?.enableCloudWatchOnStartup === true);

    const status = {
      environment: this.environment,
      cloudWatchEnabled: this.cloudWatchEnabled,
      enableConsoleOutput: this.enableConsoleOutput,
      isDevelopmentTesting,
      logQueueManagerAvailable: !!this.logQueueManager,
      contextAvailable: !!this.getContext(),
      authenticationCached: !!this.authCheckCache,
      loginInProgress: this.isLoginInProgress(),
      environmentConfig: {
        enableCloudWatchTesting: (environment as any).enableCloudWatchTesting,
        enableCloudWatchOnStartup: (environment as any).logging?.enableCloudWatchOnStartup
      }
    };

    // Only log in development environment
    if (this.environment === 'dev') {
      this.originalConsole.log('=== LOGGING STATUS ===');
      this.safeTable(status);
    }
    return status;
  }

  /**
   * Debug log transmission by sending a test log
   */
  debugLogTransmission(): void {
    // Only log in development environment
    if (this.environment === 'dev') {
      this.originalConsole.log('=== DEBUGGING LOG TRANSMISSION ===');
    }
    this.checkLoggingStatus();
    
    // Only log in development environment
    if (this.environment === 'dev') {
      this.originalConsole.log('Sending test log...');
    }
    console.log('TEST LOG: This should appear in CloudWatch', { 
      timestamp: new Date().toISOString(),
      test: true 
    });
    
    setTimeout(() => {
      const metrics = this.getLoggingMetrics();
      if (metrics) {
        // Only log in development environment
        if (this.environment === 'dev') {
          this.originalConsole.log('Current metrics after test log:');
        }
        this.safeTable(metrics);
      }
    }, 2000);
  }

  /**
   * Cleanup method called when the service is destroyed
   */
  ngOnDestroy(): void {
    // Signal all subscriptions to complete
    this.destroy$.next();
    this.destroy$.complete();
    
    // Stop authentication retry mechanism
    this.stopAuthenticationRetryMechanism();
    
    // Final flush of any remaining logs
    this.flushWithBeacon();
    
    this.internalLog('log', 'LoggingService destroyed - cleanup completed');
  }

  // ========================================
  // COMPREHENSIVE ERROR HANDLING AND LOGGING
  // ========================================

  /**
   * Get error message from unknown error type
   */
  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as any).message);
    }
    return String(error);
  }

  /**
   * Log internal operations for queue operations and authentication state changes
   * This method provides detailed logging for debugging and monitoring
   */
  private logInternalOperation(operation: string, details?: any, level: 'log' | 'warn' | 'error' = 'log'): void {
    const timestamp = new Date().toISOString();
    const logMessage = `[INTERNAL_OP] ${timestamp} - ${operation}`;
    
    try {
      if (details) {
        this.internalLog(level, logMessage, details);
      } else {
        this.internalLog(level, logMessage);
      }
    } catch (error) {
      // Fallback to original console if internal logging fails - only in dev
      if (this.environment === 'dev') {
        this.originalConsole.error('[INTERNAL_OP_ERROR] Failed to log internal operation:', error);
      }
    }
  }

  /**
   * Handle queue initialization errors with recovery
   */
  private handleQueueInitializationError(error: any): void {
    this.errorMetrics.queueOperationErrors++;
    this.errorMetrics.lastErrorTimestamp = new Date().toISOString();
    
    this.logInternalOperation('AuthFailureQueue initialization failed', { 
      error: this.getErrorMessage(error),
      attempt: this.errorRecoveryAttempts + 1
    }, 'error');

    // Attempt recovery with fallback queue implementation
    this.recoverFromQueueError();
  }

  /**
   * Recover from queue errors by creating a fallback implementation
   */
  private recoverFromQueueError(): void {
    if (this.errorRecoveryAttempts >= this.MAX_RECOVERY_ATTEMPTS) {
      this.logInternalOperation('Max recovery attempts reached - using minimal fallback queue', {
        attempts: this.errorRecoveryAttempts
      }, 'error');
      
      this.authFailureQueue = this.createMinimalFallbackQueue();
      return;
    }

    this.errorRecoveryAttempts++;
    this.isRecoveringFromError = true;
    this.errorMetrics.queueCorruptionRecoveries++;
    this.errorMetrics.lastRecoveryTimestamp = new Date().toISOString();

    try {
      // Attempt to create a new queue instance
      this.authFailureQueue = new AuthFailureQueueService();
      this.logInternalOperation('Queue recovery successful', {
        attempt: this.errorRecoveryAttempts
      });
      
      this.isRecoveringFromError = false;
    } catch (error) {
      this.logInternalOperation('Queue recovery failed', {
        error: this.getErrorMessage(error),
        attempt: this.errorRecoveryAttempts
      }, 'error');
      
      // Create minimal fallback queue
      this.authFailureQueue = this.createMinimalFallbackQueue();
      this.isRecoveringFromError = false;
    }
  }

  /**
   * Create a minimal fallback queue implementation for error recovery
   */
  private createMinimalFallbackQueue(): AuthFailureQueue {
    const fallbackLogs: LogRecord[] = [];
    const maxSize = 50; // Reduced size for fallback

    return {
      enqueue: (log: LogRecord) => {
        try {
          if (fallbackLogs.length >= maxSize) {
            fallbackLogs.shift(); // Remove oldest
          }
          fallbackLogs.push(log);
          this.logInternalOperation('Fallback queue enqueue', { size: fallbackLogs.length });
        } catch (error) {
          this.errorMetrics.memoryIssueRecoveries++;
          this.logInternalOperation('Fallback queue enqueue failed', { error: this.getErrorMessage(error) }, 'error');
        }
      },
      
      dequeue: () => {
        try {
          const log = fallbackLogs.shift();
          if (log) {
            this.logInternalOperation('Fallback queue dequeue', { remainingSize: fallbackLogs.length });
          }
          return log;
        } catch (error) {
          this.logInternalOperation('Fallback queue dequeue failed', { error: this.getErrorMessage(error) }, 'error');
          return undefined;
        }
      },
      
      flush: () => {
        try {
          const logs = [...fallbackLogs];
          fallbackLogs.length = 0;
          this.logInternalOperation('Fallback queue flush', { flushedCount: logs.length });
          this.errorMetrics.totalFlushedLogs += logs.length;
          return logs;
        } catch (error) {
          this.logInternalOperation('Fallback queue flush failed', { error: this.getErrorMessage(error) }, 'error');
          return [];
        }
      },
      
      clear: () => {
        try {
          const clearedCount = fallbackLogs.length;
          fallbackLogs.length = 0;
          this.logInternalOperation('Fallback queue clear', { clearedCount });
        } catch (error) {
          this.logInternalOperation('Fallback queue clear failed', { error: this.getErrorMessage(error) }, 'error');
        }
      },
      
      size: () => fallbackLogs.length,
      isEmpty: () => fallbackLogs.length === 0,
      isFull: () => fallbackLogs.length >= maxSize,
      getMaxSize: () => maxSize,
      
      getStatistics: () => {
        const now = Date.now();
        return {
          currentSize: fallbackLogs.length,
          maxSize,
          isEmpty: fallbackLogs.length === 0,
          isFull: fallbackLogs.length >= maxSize,
          oldestEntryAge: fallbackLogs.length > 0 ? now - (fallbackLogs[0]?.timestamp ? new Date(fallbackLogs[0].timestamp).getTime() : now) : undefined,
          newestEntryAge: fallbackLogs.length > 0 ? now - (fallbackLogs[fallbackLogs.length - 1]?.timestamp ? new Date(fallbackLogs[fallbackLogs.length - 1].timestamp).getTime() : now) : undefined
        };
      }
    };
  }

  /**
   * Enhanced authentication failure handling with comprehensive logging
   */
  private handleAuthenticationFailureWithLogging(logRecord: LogRecord): void {
    try {
      this.errorMetrics.totalAuthFailures++;
      this.errorMetrics.totalQueuedLogs++;
      
      this.logInternalOperation('Authentication failure detected', {
        logLevel: logRecord.level,
        messagePreview: typeof logRecord.message === 'string' ? logRecord.message.substring(0, 100) : 'object',
        queueSizeBefore: this.authFailureQueue.size(),
        totalAuthFailures: this.errorMetrics.totalAuthFailures
      }, 'warn');

      // Attempt to enqueue with error handling
      this.safeEnqueueLog(logRecord);
      
      this.logInternalOperation('Log queued due to auth failure', {
        queueSizeAfter: this.authFailureQueue.size(),
        queueIsFull: this.authFailureQueue.isFull()
      });

    } catch (error) {
      this.errorMetrics.queueOperationErrors++;
      this.errorMetrics.lastErrorTimestamp = new Date().toISOString();
      
      this.logInternalOperation('Failed to handle authentication failure', {
        error: this.getErrorMessage(error),
        logLevel: logRecord.level
      }, 'error');

      // Attempt recovery
      this.recoverFromQueueError();
      
      // Retry enqueue after recovery
      try {
        this.safeEnqueueLog(logRecord);
      } catch (retryError) {
        this.logInternalOperation('Retry enqueue after recovery failed', {
          error: this.getErrorMessage(retryError)
        }, 'error');
      }
    }
  }

  /**
   * Safely enqueue a log with memory and corruption protection
   */
  private safeEnqueueLog(logRecord: LogRecord): void {
    try {
      // Check for memory issues before enqueuing
      if (this.detectMemoryIssues()) {
        this.handleMemoryIssues();
      }

      // Validate log record before enqueuing
      if (!this.validateLogRecord(logRecord)) {
        this.logInternalOperation('Invalid log record detected', {
          hasMessage: !!logRecord.message,
          hasLevel: !!logRecord.level,
          hasTimestamp: !!logRecord.timestamp
        }, 'warn');
        return;
      }

      this.authFailureQueue.enqueue(logRecord);
      
    } catch (error) {
      this.errorMetrics.queueOperationErrors++;
      this.logInternalOperation('Safe enqueue failed', {
        error: this.getErrorMessage(error)
      }, 'error');
      throw error;
    }
  }

  /**
   * Detect potential memory issues
   */
  private detectMemoryIssues(): boolean {
    try {
      // Check queue size against reasonable limits
      const queueSize = this.authFailureQueue.size();
      const maxSize = this.authFailureQueue.getMaxSize();
      
      // Check if queue is approaching memory limits
      if (queueSize > maxSize * 0.9) {
        return true;
      }

      // Check for performance issues (simplified check)
      const stats = this.authFailureQueue.getStatistics();
      if (stats.oldestEntryAge && stats.oldestEntryAge > 300000) { // 5 minutes
        return true;
      }

      return false;
    } catch (error) {
      this.logInternalOperation('Memory issue detection failed', {
        error: this.getErrorMessage(error)
      }, 'error');
      return true; // Assume memory issues if detection fails
    }
  }

  /**
   * Handle memory issues by clearing old entries
   */
  private handleMemoryIssues(): void {
    try {
      this.errorMetrics.memoryIssueRecoveries++;
      this.errorMetrics.lastRecoveryTimestamp = new Date().toISOString();
      
      const sizeBefore = this.authFailureQueue.size();
      
      // If the queue has a removeOldEntries method, use it
      if (typeof (this.authFailureQueue as any).removeOldEntries === 'function') {
        const removedCount = (this.authFailureQueue as any).removeOldEntries(60000); // Remove entries older than 1 minute
        this.logInternalOperation('Removed old entries for memory recovery', {
          removedCount,
          sizeBefore,
          sizeAfter: this.authFailureQueue.size()
        });
      } else {
        // Fallback: clear half the queue
        const targetSize = Math.floor(this.authFailureQueue.getMaxSize() / 2);
        const logsToKeep: LogRecord[] = [];
        
        // Keep the most recent logs
        while (this.authFailureQueue.size() > targetSize) {
          this.authFailureQueue.dequeue();
        }
        
        this.logInternalOperation('Cleared queue for memory recovery', {
          sizeBefore,
          sizeAfter: this.authFailureQueue.size(),
          targetSize
        });
      }
      
    } catch (error) {
      this.logInternalOperation('Memory issue handling failed', {
        error: this.getErrorMessage(error)
      }, 'error');
      
      // Last resort: clear the entire queue
      try {
        this.authFailureQueue.clear();
        this.logInternalOperation('Emergency queue clear due to memory issues');
      } catch (clearError) {
        this.logInternalOperation('Emergency queue clear failed', {
          error: this.getErrorMessage(clearError)
        }, 'error');
      }
    }
  }

  /**
   * Validate log record structure
   */
  private validateLogRecord(logRecord: LogRecord): boolean {
    try {
      return !!(
        logRecord &&
        logRecord.message &&
        logRecord.level &&
        logRecord.timestamp &&
        ['INFO', 'WARN', 'ERROR', 'DEBUG'].includes(logRecord.level)
      );
    } catch (error) {
      this.logInternalOperation('Log record validation failed', {
        error: this.getErrorMessage(error)
      }, 'error');
      return false;
    }
  }

  /**
   * Enhanced authentication state change logging
   */
  private logAuthenticationStateChange(from: boolean, to: boolean, context?: string): void {
    try {
      this.logInternalOperation('Authentication state change', {
        from: from ? 'authenticated' : 'unauthenticated',
        to: to ? 'authenticated' : 'unauthenticated',
        context: context || 'unknown',
        queueSize: this.authFailureQueue.size(),
        timestamp: new Date().toISOString()
      });

      // If authentication was restored, log queue flush preparation
      if (!from && to) {
        this.logInternalOperation('Authentication restored - preparing queue flush', {
          queuedLogsCount: this.authFailureQueue.size(),
          queueStatistics: this.authFailureQueue.getStatistics()
        });
      }

      // If authentication was lost, log queue preparation
      if (from && !to) {
        this.logInternalOperation('Authentication lost - logs will be queued', {
          currentQueueSize: this.authFailureQueue.size()
        }, 'warn');
      }

    } catch (error) {
      this.errorMetrics.authenticationStateErrors++;
      this.errorMetrics.lastErrorTimestamp = new Date().toISOString();
      
      this.logInternalOperation('Failed to log authentication state change', {
        error: this.getErrorMessage(error),
        from,
        to,
        context
      }, 'error');
    }
  }

  /**
   * Get comprehensive error and metrics report
   */
  getErrorMetricsReport(): {
    errorMetrics: {
      queueOperationErrors: number;
      authenticationStateErrors: number;
      queueCorruptionRecoveries: number;
      memoryIssueRecoveries: number;
      totalAuthFailures: number;
      totalQueuedLogs: number;
      totalFlushedLogs: number;
      lastErrorTimestamp: string | null;
      lastRecoveryTimestamp: string | null;
    };
    queueStatistics: ReturnType<AuthFailureQueue['getStatistics']>;
    systemHealth: {
      isRecovering: boolean;
      recoveryAttempts: number;
      lastHealthCheck: string;
    };
    recommendations: string[];
  } {
    try {
      const queueStats = this.authFailureQueue.getStatistics();
      const recommendations: string[] = [];

      // Generate recommendations based on metrics
      if (this.errorMetrics.queueOperationErrors > 10) {
        recommendations.push('High queue operation errors detected - consider system restart');
      }
      
      if (this.errorMetrics.totalAuthFailures > 50) {
        recommendations.push('High authentication failure rate - check authentication service');
      }
      
      if (queueStats.currentSize > queueStats.maxSize * 0.8) {
        recommendations.push('Queue approaching capacity - consider increasing flush frequency');
      }
      
      if (this.errorMetrics.memoryIssueRecoveries > 5) {
        recommendations.push('Frequent memory issues - consider reducing queue size or increasing flush frequency');
      }

      return {
        errorMetrics: { ...this.errorMetrics },
        queueStatistics: queueStats,
        systemHealth: {
          isRecovering: this.isRecoveringFromError,
          recoveryAttempts: this.errorRecoveryAttempts,
          lastHealthCheck: new Date().toISOString()
        },
        recommendations
      };
      
    } catch (error) {
      this.logInternalOperation('Failed to generate error metrics report', {
        error: this.getErrorMessage(error)
      }, 'error');
      
      return {
        errorMetrics: { ...this.errorMetrics },
        queueStatistics: {
          currentSize: 0,
          maxSize: 0,
          isEmpty: true,
          isFull: false
        },
        systemHealth: {
          isRecovering: this.isRecoveringFromError,
          recoveryAttempts: this.errorRecoveryAttempts,
          lastHealthCheck: new Date().toISOString()
        },
        recommendations: ['Error metrics report generation failed - system may be in degraded state']
      };
    }
  }

  /**
   * Reset error metrics (for testing and recovery)
   */
  resetErrorMetrics(): void {
    this.logInternalOperation('Resetting error metrics');
    
    this.errorMetrics = {
      queueOperationErrors: 0,
      authenticationStateErrors: 0,
      queueCorruptionRecoveries: 0,
      memoryIssueRecoveries: 0,
      totalAuthFailures: 0,
      totalQueuedLogs: 0,
      totalFlushedLogs: 0,
      lastErrorTimestamp: null,
      lastRecoveryTimestamp: null
    };
    
    this.errorRecoveryAttempts = 0;
    this.isRecoveringFromError = false;
  }

  /**
   * Perform system health check
   */
  performHealthCheck(): {
    status: 'healthy' | 'degraded' | 'critical';
    issues: string[];
    metrics: {
      queueOperationErrors: number;
      authenticationStateErrors: number;
      queueCorruptionRecoveries: number;
      memoryIssueRecoveries: number;
      totalAuthFailures: number;
      totalQueuedLogs: number;
      totalFlushedLogs: number;
      lastErrorTimestamp: string | null;
      lastRecoveryTimestamp: string | null;
    };
  } {
    const issues: string[] = [];
    let status: 'healthy' | 'degraded' | 'critical' = 'healthy';

    try {
      // Check error rates
      if (this.errorMetrics.queueOperationErrors > 20) {
        issues.push('High queue operation error rate');
        status = 'critical';
      } else if (this.errorMetrics.queueOperationErrors > 5) {
        issues.push('Elevated queue operation errors');
        status = 'degraded';
      }

      // Check authentication failures
      if (this.errorMetrics.totalAuthFailures > 100) {
        issues.push('Very high authentication failure rate');
        status = 'critical';
      } else if (this.errorMetrics.totalAuthFailures > 20) {
        issues.push('High authentication failure rate');
        if (status === 'healthy') status = 'degraded';
      }

      // Check recovery attempts
      if (this.errorRecoveryAttempts >= this.MAX_RECOVERY_ATTEMPTS) {
        issues.push('Maximum recovery attempts reached');
        status = 'critical';
      } else if (this.errorRecoveryAttempts > 0) {
        issues.push('System has performed error recovery');
        if (status === 'healthy') status = 'degraded';
      }

      // Check queue health
      const queueStats = this.authFailureQueue.getStatistics();
      if (queueStats.isFull) {
        issues.push('Authentication failure queue is full');
        status = 'critical';
      } else if (queueStats.currentSize > queueStats.maxSize * 0.8) {
        issues.push('Authentication failure queue is near capacity');
        if (status === 'healthy') status = 'degraded';
      }

      this.logInternalOperation('Health check completed', {
        status,
        issueCount: issues.length,
        queueSize: queueStats.currentSize
      });

      return {
        status,
        issues,
        metrics: { ...this.errorMetrics }
      };

    } catch (error) {
      this.logInternalOperation('Health check failed', {
        error: this.getErrorMessage(error)
      }, 'error');

      return {
        status: 'critical',
        issues: ['Health check system failure'],
        metrics: { ...this.errorMetrics }
      };
    }
  }
}